import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useT } from "../i18n";
import { useSitzung } from "../schale/Sitzung";
import { nachschlagenOeffnen } from "../schale/nachschlagen";
import { FehlerBand, Laedt } from "../bausteine";
import { Abschnitt, Leersatz, Zeilenblatt } from "../bausteine/Handyformen";
import {
  Aufgabenzeile, Fristzeile, Haktzeile, Heutezeile, Pruefzeile, Vorlagezeile,
} from "./handy/Startzeilen";
import { useZiehauffrischen } from "../bausteine/Ziehauffrischen";
import { Installhinweis, Netzband, Warteschlangenband } from "../bausteine/Offline";
import { useStart, useWiedervorlageErledigt, useWiedervorlagen } from "../daten/haken";
import { ladestandVon, leersatzFuer } from "../daten/ladestand";
import { Tageskopf } from "./handy/Tageskopf";
import { OhneRolle, PrueferStart, Tagesmappe, Vergessenband } from "./Handy";
import { NotizBlatt } from "./handy/Notiz";
import { Wegeliste } from "./handy/Wege";
import { Aufnahmeaufgaben, aufnahmenFuerMich } from "./handy/Aufnahmeaufgaben";
import { Zuverteilen } from "./handy/Zuverteilen";
import "../stile/handy.css";
/* `mappe.css` und `handy-vertrieb.css` standen hier für „zuletzt besucht"
   und die alte Wiedervorlagenzeile. Beide sind seit dem 18.09.2026 nicht
   mehr auf diesem Schirm; die Masken, die sie noch zeichnen, bringen ihre
   Stile selbst mit (`masken/handy/Notiz.tsx`, `vorgaenge/AufnahmenTelefon.tsx`). */

/* ══════════════════════════════════════════════════════════════════
   Start am Telefon — derselbe Weg, der Schirm der Rolle.

   Luca, 17.09.2026: *„Wir wollen die Mobile-App ja nicht an die Adresse
   binden, sondern daran, dass wir von dem Mobile aus arbeiten."*

   **Bis hierher gab es den Startschirm zweimal:** `/start` am
   Schreibtisch, `/handy/start` am Telefon (und für den reinen Verkauf
   `/handy` selbst). Der Wegwächter schickte `/start` am Telefon auf
   `/handy`. Jetzt zeichnet `/start` sich je Lage selbst
   (`masken/Heute.tsx`), und `/handy/start` leitet dauerhaft hierher
   (40-mobiles-grundgeruest.md, Schritt 4.2).

   **Was darin aufgegangen ist:** die Wiedervorlagen
   (`handy/Vertrieb.tsx`), „zuletzt besucht" (`handy/Mappe.tsx`),
   Nachschlagen und Notiz (`handy/Start.tsx`). **Was dazukommt**, ist das,
   was am Schreibtisch auf der Startseite steht — nicht als vier gleich
   hohe Karten, sondern als Abschnitte untereinander, und ein leerer
   Abschnitt verschwindet (09-mobile.md, „Eine Zeilenform").

   ── Die Reihenfolge — neu geordnet am 18.09.2026 ──────────────────

   Luca, nach dem Durchgang auf 390 × 844: *„Was steht heute an? Rein,
   raus. … Heißt: Heute kann nach oben wandern, direkt quasi über die
   Wiedervorlage, danach ‚was zu tun'."*

   1. **Anfangen** — vermieten (Regel 10: der Regelfall vorn) und ein
      Angebot schreiben. Vermieten nur, wer es darf.
   2. **Zwischendurch** — Nachschlagen und Notiz.
   3. **Heute** — was hinausgeht und was zurückkommt, als Auszug.
   4. **Wiedervorlage** — nur, wenn es eine gibt.
   5. **Was ist zu tun** und **Zu prüfen**, darunter **Was hakt**.

   ── Die Machart — 18.09.2026, abends ──────────────────────────────

   Luca: *„Und Start. Es ist immer noch sehr schlecht vorbereitet. Da
   musst du auch noch mal unbedingt arbeiten: Stück für Stück durch, guck
   einfach mal von der Machart."* Als Vorbild die Handyfassung von OVIS
   (42-vorbild-ovis.md). Vier Dinge sind daraufhin anders:

   | | vorher | jetzt |
   |---|---|---|
   | Der Name | „Start" im Maskenkopf, darunter „Fr 18.09." in Mono | große Überschrift **im Inhalt**, darunter „Freitag, 18. September" |
   | Die Abschnitte | drei Blöcke oben ganz ohne Überschrift | **jeder Block heißt, wie er heißt** — „Anfangen", „Zwischendurch" |
   | Die Auskunft | die Zahl als gefüllte Pille hinter dem Wort | **rechts auf derselben Zeile**, und sie sagt mehr: „2 raus · 1 rein" |
   | Leer | „Heute" verschwand, und ganz unten stand ein Ersatzsatz | **„Heute" bleibt stehen** und trägt eine Karte mit einem Satz |

   **„Heute" ist hier ein Auszug und kein zweiter Tagesplan.** Der volle
   Tag steht in der Tagesmappe (`/heute`, unten in der Leiste); hier
   stehen die nächsten vier Zeilen und daneben der leise Weg „alle". Wer
   die Mappe nicht hat — der reine Außendienst —, sieht alle Zeilen: Was
   niemand aufschlagen kann, wird auch nicht gekürzt.

   ── Was am Telefon NICHT mehr steht (18.09.2026) ──────────────────

   Luca: *„‚Auslastung heute', ‚draußen', ‚Angebote offen': das muss auf
   Mobil in Start nicht sichtbar sein. ‚Zuletzt besucht' braucht da auch
   nicht zu sein."*

   Die drei Zahlen sind Steuerung des Innendiensts und keine Arbeit im
   Auto; am Schreibtisch stehen sie weiter (`masken/Heute.tsx`). „Zuletzt
   besucht" ist damit ganz vom Schirm — der Weg zum Kunden geht über
   Nachschlagen und über „Kunden" in der Leiste.

   **Das Nachschlagen ist kein Weg mehr, sondern das Blatt der Anwendung**
   (`masken/Nachschlagen.tsx`): Es geht über dieser Maske auf und lässt sie
   stehen.

   Klassen: `st-*`, gegen alle Stile geprüft.
   ══════════════════════════════════════════════════════════════════ */

/** Wie viele Zeilen „Heute" zeigt, bevor es auf die Tagesmappe verweist. */
const AUSZUG = 4;

/* Der eigene `Abschnitt` stand hier — Überschrift, Zahl als gefüllte Pille
   dahinter, „alle" rechts. Er ist seit dem 18.09.2026 der geteilte Baustein
   (`bausteine/Handyformen.tsx`): Überschrift links, **Auskunft rechts auf
   derselben Zeile**, und die Auskunft darf mehr sagen als eine Ziffer.
   Dieselbe Form trägt die Kundenakte, die Tagesmappe und die
   Ortsaufnahmen — 42-vorbild-ovis.md. */

/* ── Die Wiedervorlage war bis zum 19.09.2026 eine eigene Zeile hier, mit
   zwei Knöpfen darin („Angebot V-…", „Erledigt"). Sie ist jetzt eine Zeile
   wie jede andere und trägt ihre Griffe im Blatt (`handy/Startzeilen.tsx`,
   `Vorlagezeile`). ── */

/**
 * Die Wiedervorlagen — **nur die, deren Tag gekommen ist.**
 *
 * Luca, 21.09.2026: *„Eine Wiedervorlage muss aber ja erst unter meine
 * Aufgaben auftauchen, wenn der Tag auch gekommen ist, nicht vorher."*
 * `faellig` heißt heute oder überfällig (`daten/typen.ts`). Was nächsten
 * Dienstag dran ist, steht heute in keiner Aufgabenliste — es wäre eine
 * Zeile, die man jeden Tag liest und jeden Tag wegsieht; zu finden ist sie
 * unter „Nachfassen" (`/nachfassen`).
 *
 * Dieser Abschnitt steht nur da, wo es **keinen Tag** gibt (Verkauf,
 * Buchhaltung). Wer einen hat, findet dieselben Zeilen in „Meine Aufgaben"
 * unter seinen Einsätzen (`masken/Handy.tsx`).
 */
function Wiedervorlagen() {
  const t = useT();
  const { data } = useWiedervorlagen();
  const erledigen = useWiedervorlageErledigt();
  const faellige = (data ?? []).filter((v) => v.faellig);
  if (!faellige.length) return null;
  return (
    <Abschnitt
      titel={t("aufgaben.meineTitel")}
      auskunft={t("handyBlock.offen", { zahl: faellige.length })}
    >
      <Zeilenblatt>
        {faellige.map((v) => (
          <Vorlagezeile
            key={v.id}
            v={v}
            laeuft={erledigen.isPending}
            erledigen={(id, fertig) => erledigen.mutate(id, { onSuccess: fertig })}
          />
        ))}
      </Zeilenblatt>
    </Abschnitt>
  );
}

/**
 * **Heute — was hinausgeht und was zurückkommt** (18.09.2026).
 *
 * Luca: *„Was steht heute an? Rein, raus."* Dieselben Zeilen wie in der
 * Karte am Schreibtisch (`bausteine/Aufgaben.tsx`), und jede führt in ihren
 * Vorgang. Der volle Tag bleibt die Tagesmappe in der unteren Leiste — hier
 * steht der Auszug und daneben der Weg dorthin.
 *
 * **Dieser Abschnitt verschwindet nicht, wenn nichts ansteht** (abends am
 * 18.09.2026). Jeder andere tut es — ein leerer Kasten fragt nach etwas, das
 * es nicht gibt. „Heute" ist der eine, der den Schirm trägt: Er beantwortet
 * die Frage, mit der man ihn öffnet, und *„Heute geht nichts raus und kommt
 * nichts rein."* ist darauf eine Antwort. Ein Schirm, der die Frage
 * stillschweigend übergeht, ist keine.
 *
 * Das ist der Leerzustand des Vorbilds: **eine Karte mit einem Satz**, unter
 * derselben Überschrift, an derselben Stelle (42-vorbild-ovis.md).
 */
function Heute() {
  const t = useT();
  const { darf } = useSitzung();
  const abfrage = useStart();
  const alle = abfrage.data?.heute ?? [];

  /* Die Tagesmappe hat, wer draußen arbeitet oder disponiert. Der reine
     Außendienst hat sie nicht — ihm den Auszug zu kürzen hieße, ihm Zeilen
     wegzunehmen, die er nirgends nachschlagen kann (`masken/Handy.tsx`). */
  const mappe = darf(["technik", "disposition", "verwaltung"]);
  const gekuerzt = mappe && alle.length > AUSZUG;
  const zeilen = mappe ? alle.slice(0, AUSZUG) : alle;

  /* **Die Auskunft rechts sagt die Richtung, nicht die Menge.** „2 raus ·
     1 rein" ist genauso breit wie „3" und beantwortet Lucas Frage vom
     Morgen (*„Was steht heute an? Rein, raus."*), ohne dass man eine Zeile
     lesen muss. Ist die Liste gekürzt, gewinnt die andere Auskunft: Dann
     ist die wichtigere Aussage, dass nicht alles dasteht. */
  const rein = alle.filter((z) => z.art === "rein").length;
  const raus = alle.length - rein;
  const auskunft = !alle.length
    ? ""
    : gekuerzt
      ? t("handyBlock.vonZahl", { zeigt: zeilen.length, alle: alle.length })
      : [
        raus ? t("handyBlock.raus", { zahl: raus }) : "",
        rein ? t("handyBlock.rein", { zahl: rein }) : "",
      ].filter(Boolean).join(" · ");

  return (
    <Abschnitt
      titel={t("start.heuteTitel")}
      auskunft={auskunft}
      {...(gekuerzt ? { weg: "/heute", wegWort: t("handyLeiste.alle") } : {})}
    >
      {alle.length ? (
        <Zeilenblatt>
          {zeilen.map((z) => <Heutezeile key={z.id} z={z} />)}
        </Zeilenblatt>
      ) : (
        /* „Heute geht nichts raus …" nur mit einer Antwort von `GET /start`
           (N11-01) — vorher „Wird geholt …", nie ein falscher Leerzustand. */
        <Leersatz>{t(leersatzFuer(ladestandVon(abfrage), "start.heuteLeer"))}</Leersatz>
      )}
    </Abschnitt>
  );
}

/**
 * **Die offenen Ortsaufnahmen** — 19.09.2026.
 *
 * Luca: *„Wenn wir eine offene Ortsaufnahme haben, dann muss die Aufgabe ja
 * schön auf dem Handy auch auftauchen."* Für den Außendienst ist der Start
 * der Schirm, den er morgens aufschlägt — eine Tagesmappe hat er nicht. Die
 * Aufnahme stand bis heute unter „Was ist zu tun?" als Zeile eines Einsatzes
 * (Vorgangsnummer groß, der Kunde zweimal, die Einheit nirgends) und führte
 * in die Liste statt in die Begehung.
 *
 * **Nach der Wiedervorlage und vor „Was ist zu tun?"**: Was ein Datum hat,
 * steht oben; eine Aufnahme hat keins und wird mitgenommen, wenn es passt.
 * Dieselbe Abfrage wie die Abschnitte darum (`GET /start`), derselbe
 * Baustein wie auf „Heute" (`handy/Aufnahmeaufgaben.tsx`).
 */
function Ortsaufnahmen() {
  const { data } = useStart();
  return <Aufnahmeaufgaben aufgaben={data?.aufgaben} />;
}

/**
 * Was die Startseite am Schreibtisch in vier Karten zeigt — hier als
 * Abschnitte. **Seit dem 19.09.2026 nicht mehr dieselben Zeilen** wie am
 * Schreibtisch: Dort ist die Spaltenform der Karten richtig, hier ist jede
 * Zeile ein `Zweizeiler` mit ihrer Handlung im Blatt
 * (`handy/Startzeilen.tsx` — Luca: *„diese Einträge in der Liste immer
 * gleich hoch"*). Was darin steht, ist dasselbe.
 */
function Tageslage() {
  const t = useT();
  const { darf, darfAuf } = useSitzung();
  const { data, isPending, isError, error, refetch } = useStart();

  if (isPending) return <Laedt zeilen={4} />;
  if (isError || !data) return <FehlerBand fehler={error} nochmal={() => void refetch()} />;

  /* **Die Ortsaufnahmen, die im eigenen Abschnitt stehen, stehen hier nicht
     noch einmal** (19.09.2026, `Ortsaufnahmen` weiter unten). Was bleibt,
     ist die Aufnahme eines Kollegen — für die Disposition eine Auskunft,
     die sonst am Telefon nirgends steht. */
  const eigene = new Set(aufnahmenFuerMich(data.aufgaben).map((a) => a.id));
  const aufgaben = darfAuf("/aufnahmen")
    ? data.aufgaben.filter((a) => !eigene.has(a.id))
    : data.aufgaben;
  const tun = aufgaben.length + data.wiedervorlage.length;

  return (
    <>
      {tun ? (
        <Abschnitt titel={t("aufgaben.titel")} auskunft={t("handyBlock.offen", { zahl: tun })}>
          <Zeilenblatt>
            {aufgaben.map((a) => (
              <Aufgabenzeile key={a.id} a={a} lenkt={darf(["disposition", "verwaltung"])} />
            ))}
            {/* Auch die Frist führt irgendwohin — in das Brett, wenn eine
                Option abläuft, sonst zur Ressource (18.09.2026). */}
            {data.wiedervorlage.map((z) => <Fristzeile key={z.id} z={z} />)}
          </Zeilenblatt>
        </Abschnitt>
      ) : null}

      {/* ── „Zu prüfen", neu gebaut am 18.09.2026 ──────────────────────
          Luca: *„Zu ‚Zu prüfen' hast du es komplett verkackt. Die Einträge
          … müssen überarbeitet werden."* Dieselben vier Arten wie am
          Schreibtisch, jetzt aber in der Form, die er über „Was ist zu tun"
          gesagt hat (*„Das ist grundsätzlich gar nicht so schlecht
          gemacht"*): Marke und Alter links, Vorgang und Kunde rechts, und
          darunter **der Satz, der sagt, was fehlt** — er stand bis heute im
          `title`, und am Finger gibt es kein Überfahren
          (`bausteine/Aufgaben.tsx`, `Pruefzeilenknopf`). */}
      {data.zu_pruefen_zeilen.length ? (
        <Abschnitt
          titel={t("start.zuPruefen")}
          auskunft={t(data.zu_pruefen_zeilen.length === 1 ? "handyBlock.wartet" : "handyBlock.warten",
            { zahl: data.zu_pruefen_zeilen.length })}
        >
          <Zeilenblatt>
            {data.zu_pruefen_zeilen.map((v) => (
              <Pruefzeile key={`${v.art}-${v.nr}`} z={v} />
            ))}
          </Zeilenblatt>
        </Abschnitt>
      ) : null}

      {data.hakt.length ? (
        <Abschnitt
          titel={t("start.wasHakt")}
          auskunft={t("handyBlock.offen", { zahl: data.hakt.length })}
        >
          <Zeilenblatt>
            {/* Am Telefon gibt es die Planung nicht (18.09.2026) — eine Zeile,
                die dorthin zeigte, bleibt Text (`Haktzeile`). */}
            {data.hakt.map((h) => <Haktzeile key={h.id} h={h} />)}
          </Zeilenblatt>
        </Abschnitt>
      ) : null}

      {/* „Heute" stand hier unten und steht seit dem 18.09.2026 oben, über
          der Wiedervorlage (`Heute`, weiter oben in dieser Datei).

          ── Der Satz „Nichts fällig" ist weg (18.09.2026, abends) ──────
          Hier stand eine Karte für den Fall, dass der ganze Schirm leer ist
          — „Nichts fällig · Keine Aufgabe, nichts zu prüfen, nichts, was
          hakt." Sie ist überflüssig geworden: **„Heute" bleibt jetzt immer
          stehen** und trägt seinen eigenen Satz. Zwei Leermeldungen
          untereinander sagen dasselbe zweimal, und die zweite steht dann
          ohne Überschrift auf dem Tisch. */}

      {/* ── Die drei Zahlen sind weg — 18.09.2026 ─────────────────────
          Hier standen „Auslastung heute", „Draußen" und „Angebote offen"
          als drei Kacheln nebeneinander. Luca, beim Durchgang: *„das muss
          auf Mobil in Start nicht sichtbar sein."*

          Sie sind Steuerung und keine Arbeit: Wer am Telefon auf die
          Startseite sieht, will wissen, was er anfassen muss — nicht, wie
          der Park insgesamt ausgelastet ist. **Am Schreibtisch stehen sie
          unverändert** (`masken/Heute.tsx`, `.kennzahlen`), und das ist
          die ganze Unterscheidung: eine Maske, zwei Zeichnungen, keine
          zweite Schwelle (`schale/geraet.ts`). */}
    </>
  );
}

export function HeuteTelefon() {
  useZiehauffrischen();
  const t = useT();
  const gehZu = useNavigate();
  const { darf, darfAuf, darfRecht } = useSitzung();
  const [notiz, setzeNotiz] = useState(false);
  const vermietet = darfAuf("/vermieten");
  /* ── Ein Modul, drei Zuschnitte — 21.09.2026 ──
     Luca: *„Manchmal wird ein Modul, und dann, je nach Rolle, verändert sich
     das Modul, weil es einfach besser aussieht. Dann kann ja auch derjenige,
     der alle Rechte hat, das ja auch gerne alles sehen."*

     | wer | sieht |
     |---|---|
     | technik (nur `tagesmappe`) | den Tag — was vorher unter `/heute` stand |
     | aussendienst, buchhaltung (nur `start`) | Anfangen, Heute als Auszug, die Arbeit — was vorher unter `/start` stand |
     | disposition, verwaltung (beides) | **einen** Schirm: Anfangen, den ganzen Tag samt „wer hat was", die Arbeit |

     Die Rechte sind dabei nicht neu erfunden: `tagesmappe` und `start`
     trennen seit dem 18.09.2026 genau diese beiden Hälften
     (`schale/rechte.ts`). Neu ist, dass sie nicht mehr zwei Schirme
     bedeuten, sondern zwei Abschnitte auf einem. */
  const derTag = darfRecht("tagesmappe");
  const dieArbeit = darfRecht("start");

  /* Der Prüfer hat weder Tag noch Arbeit — aber eine Rolle (P5-03). */
  if (!derTag && !dieArbeit) return darfRecht("pruefen") ? <PrueferStart /> : <OhneRolle />;

  return (
    <>
      <div className="handy st-raum">
        {/* **Der Name der Maske steht im Inhalt** (18.09.2026, abends) — als
            große Überschrift mit dem Tag darunter, wie beim Vorbild
            (`handy/Tageskopf.tsx`, 42-vorbild-ovis.md). Ohne `<Kopf>` bleibt
            das Portalziel leer, und die Maske gewinnt rund 90 px. */}
        <Tageskopf titel="handy.titel" />
        <Netzband />
        <Warteschlangenband />
        <Installhinweis />

        {/* **Jeder Block hat jetzt einen Namen** (18.09.2026, abends). Bis
            dahin begann der Schirm mit zwei großen Kacheln und einer Karte,
            alle drei ohne Überschrift — man sah drei Flächen und musste sie
            lesen, um zu wissen, wozu sie da sind. Beim Vorbild ist jeder
            Block benannt, und genau daher kommt der aufgeräumte Eindruck. */}
        {/* **Der offene Stempel von gestern steht ganz oben** — er ist die
            einzige Stelle, die ungefragt sagt, dass gestern nicht
            ausgestempelt wurde (`masken/Handy.tsx`). */}
        {derTag ? <Vergessenband /> : null}

        {dieArbeit ? (
        <Abschnitt titel={t("start.anfangen")}>
          <div className="st-anfangen">
            {vermietet ? (
              <button className="losgehen haupt" type="button" onClick={() => void gehZu({ to: "/vermieten" })}>
                <span className="losgehen-was">{t("start.vermietenWas")}</span>
                <span className="losgehen-wie">{t("start.vermietenWie")}</span>
              </button>
            ) : null}
            {/* Am Telefon zeichnet `/angebote/neu` selbst das Angebot in vier
                Griffen (`masken/vorgaenge/AngebotNeu.tsx`,
                40-mobiles-grundgeruest.md, Frage 5). Der Knopf zeigt auf die
                eine Adresse; welche Fassung dort steht, entscheidet die Maske. */}
            {darfAuf("/angebote/neu") ? (
              <button className="losgehen" type="button" onClick={() => void gehZu({ to: "/angebote/neu" })}>
                <span className="losgehen-was">{t("start.angebotWas")}</span>
                <span className="losgehen-wie">{t("start.angebotWie")}</span>
              </button>
            ) : null}
          </div>
        </Abschnitt>

        ) : null}

        {dieArbeit ? (
        <Abschnitt titel={t("start.zwischendurch")}>
          <Wegeliste eintraege={[
            {
              tun: nachschlagenOeffnen,
              text: "handyLeiste.nachschlagen",
              satz: "handyLeiste.nachschlagenSatz",
              zeichen: "nachschlagen",
            },
            {
              tun: () => setzeNotiz(true),
              text: "handyVertrieb.notiz",
              satz: "handyStart.notizSatz",
              zeichen: "notiz",
            },
          ]} />
        </Abschnitt>
        ) : null}

        {/* **Der Tag selbst** (21.09.2026) — dieselben Zeilen, die bis hierher
            unter einem eigenen Fach standen: „Läuft jetzt", „Meine", „Frei
            zum Übernehmen" und, für die Disposition, **was andere haben, mit
            ihrem Namen**. Genau das war Lucas Frage: *„Das ist ja auch: Wer
            hat welche Aufträge heute?"* */}
        {/* **„Zu verteilen"** (25.09.2026): Am Schreibtisch wird im Brett
            verteilt, das es am Telefon nicht gibt — hier dieselben Marken,
            ein Tipp öffnet das Blatt mit den Leuten (`handy/Zuverteilen.tsx`). */}
        {darf(["disposition", "verwaltung"]) ? <Zuverteilen /> : null}

        {derTag ? <Tagesmappe /> : null}

        {/* **Ohne Tagesmappe bleibt der Auszug** — vier Zeilen „raus/rein"
            aus derselben Abfrage (`GET /start`). Wer den Tag ganz sieht,
            braucht ihn nicht: Er stünde ein zweites Mal da, und genau das
            war der Anlass für diesen Umbau. */}
        {dieArbeit && !derTag ? <Heute /> : null}

        {/* **Die Wiedervorlagen stehen in „Meine Aufgaben"** (21.09.2026) —
            dort, wo auch die Einsätze stehen, die mir gehören
            (`masken/Handy.tsx`). Wer keinen Tag hat (Verkauf, Buchhaltung),
            bekommt sie hier als eigenen Abschnitt, unter demselben Namen:
            Zwei Listen für „was liegt bei mir" wären eine zu viel, keine
            Liste wäre eine zu wenig. */}
        {dieArbeit && !derTag ? <Wiedervorlagen /> : null}

        {/* **Die Ortsaufnahmen stehen einmal.** Die Tagesmappe bringt sie
            selbst mit (`masken/Handy.tsx`) — sie sind Arbeit an diesem Tag.
            Wer den Tag nicht sieht, bekommt sie hier; beides nebeneinander
            war beim ersten Durchgang dieses Moduls am Gerät zu sehen und war
            genau die Doppelung, gegen die es gebaut wurde. */}
        {derTag ? null : <Ortsaufnahmen />}
        {dieArbeit ? <Tageslage /> : null}
      </div>

      <NotizBlatt offen={notiz} schliessen={() => setzeNotiz(false)} />
    </>
  );
}
